import React, { useEffect, useState } from 'react';
import api from '../api/api';
import { TableSkeleton, EmptyState } from '../components';
import WecomConversationDrawer, {
  CopyButton
} from '../components/WecomConversationDrawer';
import { PageHeader } from '../components/layout';
import { mergeOlderLogs, shortenWecomId } from '../utils/wecomChatLogs';

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('zh-CN', { hour12: false });
};

const getBotLabel = (item) => (
  item.bot_name || item.bot_id || `Bot ${item.bot_config_id}`
);

const WecomChatLogsPage = () => {
  const [conversations, setConversations] = useState([]);
  const [botConfigs, setBotConfigs] = useState([]);
  const [conversationMeta, setConversationMeta] = useState({
    total: 0,
    page: 1,
    total_pages: 1,
    page_size: 20
  });
  const [conversationFilters, setConversationFilters] = useState({
    wecom_user_id: '',
    conversation_id: '',
    bot_config_id: '',
    direction: '',
    status: '',
    issue_key: ''
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [detailLogs, setDetailLogs] = useState([]);
  const [detailMeta, setDetailMeta] = useState({
    has_more: false,
    next_before_id: null
  });
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailLoadingOlder, setDetailLoadingOlder] = useState(false);
  const [detailError, setDetailError] = useState(null);

  const fetchConversations = async (page = conversationMeta.page) => {
    setLoading(true);
    try {
      const params = { page, page_size: conversationMeta.page_size };
      Object.entries(conversationFilters).forEach(([key, value]) => {
        if (value) params[key] = value;
      });
      const response = await api.getWecomChatConversations(params);
      setConversations(response.data.items || []);
      setConversationMeta({
        total: response.data.total || 0,
        page: response.data.page || page,
        total_pages: response.data.total_pages || 1,
        page_size: response.data.page_size || conversationMeta.page_size
      });
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchConversationDetail = async (conversation, beforeId = null) => {
    const loadingOlder = beforeId != null;
    if (loadingOlder) {
      setDetailLoadingOlder(true);
    } else {
      setDetailLoading(true);
      setDetailLogs([]);
    }
    try {
      const params = {
        bot_config_id: conversation.bot_config_id,
        page_size: 50
      };
      if (beforeId != null) params.before_id = beforeId;
      const response = await api.getWecomChatConversationLogs(
        conversation.conversation_id,
        params
      );
      const items = response.data.items || [];
      setDetailLogs((current) => (
        loadingOlder ? mergeOlderLogs(current, items) : items
      ));
      setDetailMeta({
        has_more: Boolean(response.data.has_more),
        next_before_id: response.data.next_before_id ?? null
      });
      setDetailError(null);
    } catch (err) {
      setDetailError(err.response?.data?.detail || err.message);
    } finally {
      setDetailLoading(false);
      setDetailLoadingOlder(false);
    }
  };

  const openConversation = (conversation) => {
    setSelectedConversation(conversation);
    setDetailError(null);
    setDetailMeta({ has_more: false, next_before_id: null });
    fetchConversationDetail(conversation);
  };

  useEffect(() => {
    api.getWecomConfigs()
      .then((response) => setBotConfigs(response.data.items || []))
      .catch(() => setBotConfigs([]));
    fetchConversations(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleConversationKeyDown = (event, conversation) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openConversation(conversation);
    }
  };

  const renderStatus = (item) => {
    const currentStatus = item.current_status
      || (item.has_failed_log ? 'failed' : 'success');
    const label = currentStatus === 'failed'
      ? '异常'
      : currentStatus === 'processing'
        ? '处理中'
        : '正常';
    return (
      <span className={`wecom-conversation-status${currentStatus === 'failed' ? ' is-error' : ''}`}>
        {label}
      </span>
    );
  };

  return (
    <div className="wecom-conversation-page wecom-chat-log-page">
      <PageHeader
        title="企业微信聊天日志"
      />

      <section className="operation-log-list-surface wecom-conversation-surface">
        <div className="operation-list-surface-header wecom-conversation-card-header">
          <div>
            <h3>会话日志</h3>
            <p>筛选会话后可展开查看完整消息和 Jira 操作。</p>
          </div>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => fetchConversations(1)}
          >
            查询
          </button>
        </div>
        <div className="operation-list-surface-body">
          <div className="wecom-chat-filter-bar wecom-conversation-filters">
            <input
              className="form-input"
              name="wecom_user_id"
              placeholder="企业微信用户 ID"
              value={conversationFilters.wecom_user_id}
              onChange={(event) => setConversationFilters((current) => ({
                ...current,
                wecom_user_id: event.target.value
              }))}
            />
            <input
              className="form-input"
              name="conversation_id"
              placeholder="会话 ID"
              value={conversationFilters.conversation_id}
              onChange={(event) => setConversationFilters((current) => ({
                ...current,
                conversation_id: event.target.value
              }))}
            />
            <input
              className="form-input"
              name="issue_key"
              placeholder="工单 Key"
              value={conversationFilters.issue_key}
              onChange={(event) => setConversationFilters((current) => ({
                ...current,
                issue_key: event.target.value
              }))}
            />
            <select
              className="form-input"
              name="bot_config_id"
              value={conversationFilters.bot_config_id}
              onChange={(event) => setConversationFilters((current) => ({
                ...current,
                bot_config_id: event.target.value
              }))}
            >
              <option value="">全部 Bot</option>
              {botConfigs.map((bot) => (
                <option key={bot.id} value={bot.id}>
                  {bot.name || bot.bot_id || `Bot ${bot.id}`}
                </option>
              ))}
            </select>
            <select
              className="form-input"
              name="direction"
              value={conversationFilters.direction}
              onChange={(event) => setConversationFilters((current) => ({
                ...current,
                direction: event.target.value
              }))}
            >
              <option value="">包含任意方向</option>
              <option value="incoming">包含用户消息</option>
              <option value="outgoing">包含机器人回复</option>
              <option value="action">包含 Jira 操作</option>
              <option value="error">包含错误</option>
            </select>
            <select
              className="form-input"
              name="status"
              value={conversationFilters.status}
              onChange={(event) => setConversationFilters((current) => ({
                ...current,
                status: event.target.value
              }))}
            >
              <option value="">包含任意状态</option>
              <option value="success">包含成功日志</option>
              <option value="failed">包含失败日志</option>
            </select>
          </div>

          <div className="wecom-chat-results">
            {error && (
              <div className="wecom-list-error">
                <span>错误：{error}</span>
                <button
                  type="button"
                  className="btn btn-secondary"
                  data-action="retry-list"
                  onClick={() => fetchConversations(conversationMeta.page)}
                >
                  重试
                </button>
              </div>
            )}

            {loading ? (
              <TableSkeleton rowCount={6} columnCount={4} label="加载企业微信会话中" />
            ) : conversations.length === 0 ? (
              <EmptyState title="暂无匹配会话" description="调整筛选条件，或等待企业微信产生新的会话记录。" />
            ) : (
            <>
              <div className="wecom-conversation-table-wrap">
                <table className="table wecom-conversation-table">
                  <colgroup>
                    <col className="wecom-col-active-at" />
                    <col className="wecom-col-user" />
                    <col className="wecom-col-conversation" />
                    <col className="wecom-col-preview" />
                    <col className="wecom-col-log-count" />
                    <col className="wecom-col-issues" />
                    <col className="wecom-col-status" />
                    <col className="wecom-col-action" />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>最后活跃</th>
                      <th>用户</th>
                      <th>Bot / 会话</th>
                      <th>最新内容</th>
                      <th>日志数</th>
                      <th>Jira 工单</th>
                      <th>状态</th>
                      <th aria-label="操作" />
                    </tr>
                  </thead>
                  <tbody>
                    {conversations.map((item) => (
                      <tr
                        key={`${item.bot_config_id}:${item.conversation_id}`}
                        data-conversation-key={`${item.bot_config_id}:${item.conversation_id}`}
                        onClick={() => openConversation(item)}
                        onKeyDown={(event) => handleConversationKeyDown(event, item)}
                        tabIndex={0}
                      >
                        <td>{formatDateTime(item.last_active_at)}</td>
                        <td>
                          <span className="wecom-user-primary">
                            {item.wecom_name || shortenWecomId(item.wecom_user_id)}
                          </span>
                          <span className="wecom-id-line">
                            <span
                              className="wecom-user-secondary"
                              title={item.wecom_user_id}
                            >
                              {shortenWecomId(item.wecom_user_id)}
                            </span>
                            <CopyButton value={item.wecom_user_id} label="用户 ID" />
                          </span>
                        </td>
                        <td>
                          <span className="wecom-user-primary">{getBotLabel(item)}</span>
                          <span className="wecom-id-line">
                            <span
                              className="wecom-user-secondary"
                              title={item.conversation_id}
                            >
                              {shortenWecomId(item.conversation_id)}
                            </span>
                            <CopyButton value={item.conversation_id} label="会话 ID" />
                          </span>
                        </td>
                        <td>
                          <span className="wecom-conversation-preview">
                            {item.last_content || '无内容'}
                          </span>
                        </td>
                        <td>{item.log_count}</td>
                        <td>{(item.issue_keys || []).join(', ') || '-'}</td>
                        <td>{renderStatus(item)}</td>
                        <td>
                          <button
                            type="button"
                            className="wecom-icon-button"
                            aria-label="查看会话"
                            title="查看会话"
                            onClick={(event) => {
                              event.stopPropagation();
                              openConversation(item);
                            }}
                          >
                            <svg viewBox="0 0 24 24" aria-hidden="true">
                              <path d="M9 18l6-6-6-6" />
                            </svg>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="wecom-conversation-mobile-list">
                {conversations.map((item) => (
                  <article
                    key={`${item.bot_config_id}:${item.conversation_id}`}
                    className="wecom-conversation-mobile-item"
                    data-conversation-key={`${item.bot_config_id}:${item.conversation_id}`}
                    onClick={() => openConversation(item)}
                    onKeyDown={(event) => handleConversationKeyDown(event, item)}
                    role="button"
                    tabIndex={0}
                  >
                    <div className="wecom-mobile-primary">
                      <strong>
                        {item.wecom_name || shortenWecomId(item.wecom_user_id)}
                      </strong>
                      {renderStatus(item)}
                    </div>
                    <div className="wecom-conversation-preview">
                      {item.last_content || '无内容'}
                    </div>
                    <div className="wecom-mobile-meta">
                      <span>{formatDateTime(item.last_active_at)}</span>
                      <span>{item.log_count} 条日志</span>
                      <span>{getBotLabel(item)}</span>
                      <span>{(item.issue_keys || []).join(', ') || '无 Jira 工单'}</span>
                    </div>
                    <div className="wecom-mobile-identifiers">
                      <span className="wecom-id-line">
                        <span title={item.wecom_user_id}>
                          用户 {shortenWecomId(item.wecom_user_id)}
                        </span>
                        <CopyButton value={item.wecom_user_id} label="用户 ID" />
                      </span>
                      <span className="wecom-id-line">
                        <span title={item.conversation_id}>
                          会话 {shortenWecomId(item.conversation_id)}
                        </span>
                        <CopyButton value={item.conversation_id} label="会话 ID" />
                      </span>
                    </div>
                  </article>
                ))}
              </div>
            </>
            )}
          </div>

          <div className="wecom-conversation-pagination wecom-chat-pagination">
            <span>共 {conversationMeta.total} 个会话，保留 30 天</span>
            <div>
              <button
                type="button"
                className="btn btn-secondary"
                disabled={conversationMeta.page <= 1}
                onClick={() => fetchConversations(conversationMeta.page - 1)}
              >
                上一页
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                disabled={conversationMeta.page >= conversationMeta.total_pages}
                onClick={() => fetchConversations(conversationMeta.page + 1)}
              >
                下一页
              </button>
            </div>
          </div>
        </div>
      </section>

      <WecomConversationDrawer
        open={Boolean(selectedConversation)}
        conversation={selectedConversation}
        logs={detailLogs}
        loading={detailLoading}
        error={detailError}
        hasMore={detailMeta.has_more}
        loadingOlder={detailLoadingOlder}
        onLoadOlder={() => {
          if (selectedConversation && detailMeta.next_before_id != null) {
            fetchConversationDetail(
              selectedConversation,
              detailMeta.next_before_id
            );
          }
        }}
        onRetry={() => {
          if (selectedConversation) {
            fetchConversationDetail(selectedConversation);
          }
        }}
        onClose={() => setSelectedConversation(null)}
      />
    </div>
  );
};

export default WecomChatLogsPage;
